import type { Types } from '@cornerstonejs/core';
import {
  RenderingEngine,
  Enums,
  cache,
  ProgressiveRetrieveImages,
  utilities,
  metaData,
} from '@cornerstonejs/core';
import {
  initDemo,
  createImageIdsAndCacheMetaData,
  setTitleAndDescription,
  getLocalUrl,
} from '../../../../utils/demo/helpers';

const { imageRetrieveMetadataProvider } = utilities;
const { singleRetrieveStages, sequentialRetrieveStages } =
  ProgressiveRetrieveImages;

// This is for debugging purposes
console.warn(
  'Click on index.ts to open source code for this example --------->'
);

const { ViewportType, ImageQualityStatus } = Enums;

// ======== Set up page ======== //
setTitleAndDescription(
  'HTJ2K Basic Display - Stack',
  'Displays a single DICOM image in a Stack viewport after clicking the load button.'
);

const content = document.getElementById('content');

const instructions = document.createElement('p');
instructions.innerText = 'Click on a button to perform the given load type';
content.appendChild(instructions);

const loaders = document.createElement('div');
content.appendChild(loaders);

const timingInfo = document.createElement('div');
timingInfo.style.width = '45em';
timingInfo.style.height = '5em';
timingInfo.style.float = 'left';
timingInfo.innerText = 'Timing Info Here';
content.appendChild(timingInfo);

const devicePixelRatio = window.devicePixelRatio || 1;
const element = document.createElement('div');
element.id = 'cornerstone-element';
// Use devicePixelRatio here so that the window size fits all pixels, but not
// larger than that.
element.style.width = `${3036 / devicePixelRatio}px`;
element.style.height = `${3036 / devicePixelRatio}px`;
element.style.clear = 'both';
content.appendChild(element);

// ============================= //

const statusNames = {
  [ImageQualityStatus.FULL_RESOLUTION]: 'full resolution',
  [ImageQualityStatus.LOSSY]: 'lossy',
  [ImageQualityStatus.SUBRESOLUTION]: 'sub-resolution',
};

let startTime = Date.now();

async function newImageFunction(evt) {
  const { image } = evt.detail;
  const {
    imageQualityStatus: status,
    decodeTimeInMS,
    loadTimeInMS,
    transferSyntaxUID,
  } = image;
  const complete = status === ImageQualityStatus.FULL_RESOLUTION;
  if (complete) {
    element.removeEventListener(Enums.Events.STACK_NEW_IMAGE, newImageFunction);
  }
  const completeText = statusNames[status] || `other ${status}`;
  const totalTime = Date.now() - startTime;
  timingInfo.innerHTML += `<p style="margin:0">Render ${completeText} of ${transferSyntaxUID} load ${loadTimeInMS} ms decode ${decodeTimeInMS} ms from start ${totalTime} ms</p>`;
}

async function showStack(
  stack: string[],
  viewport,
  retrieveConfiguration,
  name: string
) {
  cache.purgeCache();
  imageRetrieveMetadataProvider.clear();
  if (retrieveConfiguration) {
    imageRetrieveMetadataProvider.add('stack', retrieveConfiguration);
  }
  timingInfo.innerHTML = `<p id="loading" style="margin:0">Loading ${name}</p>`;
  startTime = Date.now();
  element.addEventListener(Enums.Events.STACK_NEW_IMAGE, newImageFunction);
  const start = Date.now();
  // Set the stack on the viewport
  await viewport.setStack(stack, 0, retrieveConfiguration);

  // Render the image
  viewport.render();
  const end = Date.now();
  const { transferSyntaxUID } = metaData.get('transferSyntax', stack[0]);
  document.getElementById('loading').innerText = `Stack render took ${
    end - start
  } using ${transferSyntaxUID}`;
}

/**
 * The renditions these configurations retrieve are generated by static
 * DICOMweb's `createdicomweb` CLI, which replaces the old
 * `mkdicomweb create --alternate ...` invocations. `create` writes the base
 * store and `alternates` writes the rendition directories beside it:
 *
 * ```bash
 * cd /src/static-dicomweb
 * createdicomweb create "/dicom/DE Images for Rad"
 * createdicomweb alternates 1.3.6.1.4.1.9590.100.1.2.19841440611855834937505752510708699165 \
 *   --jls --jls-thumbnail --htj2k --htj2k-lossy
 * ```
 *
 * Serve the result with `dicomwebserver` and load with `?useLocal=true`.
 */

// This study's primary frames/ are not HTJ2K, so every HTJ2K configuration has
// to retrieve from the htj2k/ rendition; reading primary frames/ would exercise
// whatever the source transfer syntax happens to be instead. A retrieve type
// with no entry of its own falls back to `default`.
const htj2kFrames = { framesPath: '/htj2k/' };

// One streaming request, decoded repeatedly as the response arrives. The
// defaults apply here too: the first decode runs once `initialChunkSize` (32k)
// has accumulated, each decode after it waits for `chunkSize` (128k) more, and
// `msBetweenDecode` (500) keeps a fast connection from decoding faster than the
// display can show.
const htj2kProgressiveOptions = {
  retrieveOptions: {
    default: htj2kFrames,
    single: {
      ...htj2kFrames,
      streaming: true,
      decodeLevel: 0,
    },
  },
};

// Every stage decodes at full resolution (decodeLevel 0), including the first
// one that has only 32k of codestream. OpenJPH decodes the truncated remainder
// rather than throwing, so the sub-resolution fallback ladder this used to
// carry is gone, and the first image is full size instead of a quarter-size
// decode scaled back up.
//
// The range sizes are left at their defaults here: `initialChunkSize` 32k for
// the first range and `chunkSize` 128k for each range after it. Both are read
// from the stage that fetches the first bytes, so they belong on `singleFast`
// rather than on a later stage. `msBetweenDecode` does not apply on this path,
// because each range stage decodes once.
const htj2kByteRanges = {
  stages: [
    {
      id: 'lossySequential',
      retrieveType: 'singleFast',
    },
    {
      id: 'lossyMiddle',
      retrieveType: 'singleMiddle',
    },
    {
      id: 'finalSequential',
      retrieveType: 'singleFinal',
    },
  ],
  retrieveOptions: {
    default: htj2kFrames,
    singleFast: {
      ...htj2kFrames,
      decodeLevel: 0,
      rangeIndex: 0,
    },
    // Note how the range increases significantly to get much more data
    singleMiddle: {
      ...htj2kFrames,
      decodeLevel: 0,
      rangeIndex: 10,
    },
    singleFinal: {
      ...htj2kFrames,
      // Just do the final range retrieve
      rangeIndex: -1,
    },
  },
};

/**
 * Runs the demo
 */
async function run() {
  // Init Cornerstone and related libraries
  await initDemo();

  // Get Cornerstone imageIds and fetch metadata into RAM
  const imageIds = await createImageIdsAndCacheMetaData({
    StudyInstanceUID:
      '1.3.6.1.4.1.9590.100.1.2.19841440611855834937505752510708699165',
    SeriesInstanceUID:
      '1.3.6.1.4.1.9590.100.1.2.160160590111755920740089886004263812825',
    // This study is not on the public store; run with ?useLocal=true against a
    // local static DICOMweb server to load it.
    wadoRsRoot:
      getLocalUrl() || 'https://d14fa38qiwhyfd.cloudfront.net/dicomweb',
  });

  // Instantiate a rendering engine
  const renderingEngineId = 'myRenderingEngine';
  const renderingEngine = new RenderingEngine(renderingEngineId);

  // Create a stack viewport
  const viewportId = 'stackViewport';
  const viewportInput = {
    viewportId,
    type: ViewportType.STACK,
    element,
    defaultOptions: {
      background: <Types.Point3>[0.2, 0, 0.2],
    },
  };

  renderingEngine.enableElement(viewportInput);

  // Get the stack viewport that was created
  const viewport = <Types.IStackViewport>(
    renderingEngine.getViewport(viewportId)
  );

  const createButton = (text, action) => {
    const button = document.createElement('button');
    button.innerText = text;
    button.id = text;
    button.onclick = action;
    loaders.appendChild(button);
    return button;
  };

  const loadButton = (text, imageIds, retrieveConfiguration) => {
    return createButton(
      text,
      showStack.bind(null, imageIds, viewport, retrieveConfiguration, text)
    );
  };

  loadButton('HTJ2K Non Progressive', imageIds, undefined);
  loadButton('HTJ2K Progressive', imageIds, htj2kProgressiveOptions);
  loadButton('HTJ2K 3 Range', imageIds, htj2kByteRanges);
}

run();
